/**
 * CLOCKS registry — every statutory deadline and district policy timer.
 * Loads schema/clocks.json (the single canonical source per ADR-0017) and
 * Zod-validates it at module load. FAIL CLOSED: any invalid entry throws.
 *
 * Canon this file enforces structurally (CP-R1316/CP-R1319):
 *   - kind 'district' means POLICY, not law: it must carry an EMPTY cite.
 *   - every non-district clock's cite must RESOLVE in the CITATIONS registry —
 *     a clock's legal status is its citation's status, never self-asserted.
 *   - a clock may not cite a noDayCount authority as its basis: where the law
 *     sets no day count, any timer is district policy.
 *   - a clock whose citation is 'pending' must carry the honesty scaffolding
 *     (statutoryMax + note) so the tighter internal figure is never shown as law.
 *   - AN ENTITLEMENT IS NOT A DEADLINE: entitlement:true requires bound:'min'
 *     (a floor — the district may serve longer, never shorter).
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import { getCitation, hasCitation, type CitationEntry, type CitationStatus } from './citations'
import clocksJson from '../../../../schema/clocks.json'

export const ClockEntryUnitSchema = z.enum(['school', 'calendar', 'months', 'years'])
export type ClockEntryUnit = z.infer<typeof ClockEntryUnitSchema>

export const ClockEntryKindSchema = z.enum(['regulatory', 'district', 'practice'])
export type ClockEntryKind = z.infer<typeof ClockEntryKindSchema>

export const ClockEntryBoundSchema = z.enum(['max', 'min', 'exact'])
export type ClockEntryBound = z.infer<typeof ClockEntryBoundSchema>

export const ClockEntrySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    value: z.number().int().min(0),
    unit: ClockEntryUnitSchema,
    kind: ClockEntryKindSchema,
    bound: ClockEntryBoundSchema,
    entitlement: z.boolean().optional(),
    trigger: z.string().min(1),
    cite: z.string(),
    statutoryMax: z.number().int().positive().optional(),
    altValue: z.number().int().positive().optional(),
    altUnit: ClockEntryUnitSchema.optional(),
    configurable: z.union([
      z.literal(false),
      z.object({ key: z.string().min(1), min: z.number().int(), max: z.number().int() }).strict(),
    ]),
    armed: z.boolean(),
    note: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((c, ctx) => {
    if (c.kind === 'district' && c.cite.trim() !== '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `clock '${c.id}': kind 'district' is POLICY, not law — it must carry an EMPTY cite (no day count exists in law)`,
      })
    }
    if (c.kind !== 'district' && c.cite.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `clock '${c.id}': kind '${c.kind}' requires a citation-registry id in cite — a legal claim without a resolvable cite is unrepresentable`,
      })
    }
    if (c.entitlement === true && c.bound !== 'min') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `clock '${c.id}': AN ENTITLEMENT IS NOT A DEADLINE — entitlement:true requires bound:'min' (a floor: serve longer, never shorter)`,
      })
    }
    if (c.altValue !== undefined && c.altUnit === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `clock '${c.id}': altValue without altUnit`,
      })
    }
  })

export type ClockEntry = z.infer<typeof ClockEntrySchema>

const ClocksFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    kindMeanings: z.record(z.string()),
    boundMeanings: z.record(z.string()),
    clocks: z.array(ClockEntrySchema).min(10),
  })
  .strict()

function loadClocks(): readonly ClockEntry[] {
  const parsed = ClocksFileSchema.safeParse(clocksJson)
  if (!parsed.success) {
    throw AppError.validation(
      'CLOCKS registry failed validation — refusing to load a partial or malformed deadline registry (fail closed)',
      parsed.error.issues,
    )
  }
  const seen = new Set<string>()
  for (const c of parsed.data.clocks) {
    if (seen.has(c.id)) {
      throw AppError.validation(`CLOCKS registry: duplicate clock id '${c.id}'`)
    }
    seen.add(c.id)

    if (c.kind !== 'district') {
      if (!hasCitation(c.cite)) {
        throw AppError.validation(
          `clock '${c.id}': cite '${c.cite}' does not resolve in the CITATIONS registry — a clock's legal status is its citation's status, never self-asserted`,
        )
      }
      const citation = getCitation(c.cite)
      if (citation.noDayCount === true) {
        throw AppError.validation(
          `clock '${c.id}': cites '${c.cite}', an authority recorded as setting NO day count — where the law sets no number, a timer is district POLICY (kind:'district', cite:'')`,
        )
      }
      if (citation.status === 'pending' && (c.statutoryMax === undefined || c.note === undefined)) {
        throw AppError.validation(
          `clock '${c.id}': its citation '${c.cite}' is PENDING (not yet law) — the clock must carry statutoryMax (the rule in force) and a note so the tighter figure is never presented as the law`,
        )
      }
      if (citation.status === 'unreviewed') {
        throw AppError.validation(
          `clock '${c.id}': its citation '${c.cite}' is UNREVIEWED — an unreviewed authority claims NOTHING and cannot back a running clock`,
        )
      }
    }
  }
  return Object.freeze(parsed.data.clocks)
}

/** Every clock, validated. Frozen — the registry is read-only at runtime. */
export const CLOCKS: readonly ClockEntry[] = loadClocks()

const CLOCK_INDEX: ReadonlyMap<string, ClockEntry> = new Map(CLOCKS.map((c) => [c.id, c]))

export function hasClock(id: string): boolean {
  return CLOCK_INDEX.has(id)
}

/** Resolve a clock by id. THROWS on unknown id — deadlines resolve from the registry only (Rule 1). */
export function getClock(id: string): ClockEntry {
  const c = CLOCK_INDEX.get(id)
  if (!c) {
    throw AppError.notFound(
      `Unknown clock id '${id}' — day counts resolve from the CLOCKS registry only (Rule 1); an unresolvable clock is a build defect`,
    )
  }
  return c
}

/**
 * The `_clockDays1316` analogue — a clock's magnitude WITH its unit.
 * Never returns a bare number: a 3-'years' triennial is not 3 days.
 */
export function clockDuration(id: string): { value: number; unit: ClockEntryUnit } {
  const c = getClock(id)
  return { value: c.value, unit: c.unit }
}

export interface ClockAuthority {
  kind: ClockEntryKind
  /** null for district policy — a policy timer claims no legal authority. */
  citation: CitationEntry | null
  /** 'district-policy' when the clock is policy; otherwise the citation's status. */
  status: CitationStatus | 'district-policy'
  /** True ONLY for a regulatory clock whose citation is verified. 'pending' is NOT law. */
  isLaw: boolean
}

/**
 * What may honestly be said about a clock's legal basis. A 'pending' citation
 * yields isLaw:false — FOLLOW IT, DO NOT CITE IT AS LAW.
 */
export function clockAuthority(id: string): ClockAuthority {
  const c = getClock(id)
  if (c.kind === 'district') {
    return { kind: c.kind, citation: null, status: 'district-policy', isLaw: false }
  }
  const citation = getCitation(c.cite)
  return {
    kind: c.kind,
    citation,
    status: citation.status,
    isLaw: c.kind === 'regulatory' && citation.status === 'verified',
  }
}
